import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import get from "lodash/get";
import { bool, object } from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { disconnectObserver, initiateNewObserver } from "../../utils";
import { WithLazy } from "../with-lazy";

var BrightcoveElement = function BrightcoveElement(props) {
  var _window4;

  var _props$element = props.element,
      element = _props$element === void 0 ? {} : _props$element,
      loadIframeOnClick = props.loadIframeOnClick,
      _props$policyKey = props.policyKey,
      policyKey = _props$policyKey === void 0 ? "" : _props$policyKey;

  var _get = get(element, ["metadata"], {}),
      accountId = _get["account-id"],
      videoId = _get["video-id"],
      playerId = _get["player-id"];

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      showVideo = _useState2[0],
      setVideoToggle = _useState2[1];

  var _useState3 = useState(""),
      _useState4 = _slicedToArray(_useState3, 2),
      posterImage = _useState4[0],
      setPosterImage = _useState4[1];

  var _useState5 = useState(null),
      _useState6 = _slicedToArray(_useState5, 2),
      myPlayer = _useState6[0],
      setMyPlayer = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      thumbnailClicked = _useState8[0],
      setThumbnailClicked = _useState8[1];

  var videoRef = useRef();
  var videoPausedByObserver = useRef(); // To check if the video is not click paused by user but paused by intersection observer, Its implemented this way because we cannot capture the click-pause event as videos are playing in iframe

  var observerRef = useRef(); // To Observe video element for intersection

  var loadLibrary = function loadLibrary() {
    var _window;

    if (!((_window = window) !== null && _window !== void 0 && _window.BrightcovePlayerLoader)) {
      return import(
      /* webpackChunkName: "qtc-react-brightcove" */
      "@brightcove/react-player-loader").then(function (module) {
        window.BrightcovePlayerLoader = module["default"];
        setVideoToggle(true);
      })["catch"](function (err) {
        console.log("Failed to load @brightcove/react-player-loader", err);
        return Promise.reject();
      });
    }

    setVideoToggle(true);
  };

  useEffect(function () {
    if (!loadIframeOnClick) {
      loadLibrary();
    }
  }, [loadIframeOnClick]);
  useEffect(function () {
    if (!myPlayer) return;
    myPlayer.on("play", startObserver);
    myPlayer.on("pause", handleVideoPause);
    if (thumbnailClicked) myPlayer.play();
    return function () {
      myPlayer && myPlayer.off("play");
      myPlayer && myPlayer.off("pause");
      observerRef.current && disconnectObserver(observerRef.current);
    };
  }, [myPlayer, thumbnailClicked]);

  function intersectionCallback(entries) {
    var videoInViewPort = entries === null || entries === void 0 ? void 0 : entries[0].isIntersecting;
    if (videoInViewPort) myPlayer.play();else {
      videoPausedByObserver.current = true; // before the below line fires the pause event we set the videoPausedByObserver Ref to true, this lets the pause events callback to know that the video is not click-paused by the user but paused by the intersection observer

      myPlayer.pause();
    }
  }

  function startObserver() {
    var targetElement = videoRef.current;
    var observer = observerRef.current;

    if (observer) {
      observer.observe(targetElement);
    } else {
      var intersectionObserver = initiateNewObserver(targetElement, intersectionCallback);
      observerRef.current = intersectionObserver;
    }
  }

  function handleVideoPause() {
    var observer = observerRef.current;
    var isVideoPausedByObserver = videoPausedByObserver.current;

    if (isVideoPausedByObserver) {
      videoPausedByObserver.current = false; // This is a clean up to set videoPausedByObserver back to false
    } else {
      disconnectObserver(observer);
    }
  }

  var brightcoveIframe = function brightcoveIframe() {
    var _window2;

    var BrightcovePlayerLoader = (_window2 = window) === null || _window2 === void 0 ? void 0 : _window2.BrightcovePlayerLoader;
    return /*#__PURE__*/React.createElement(BrightcovePlayerLoader, {
      accountId: accountId,
      videoId: videoId,
      playerId: playerId,
      attrs: {
        className: "brightcove-player"
      },
      onSuccess: function onSuccess(success) {
        return setMyPlayer(success.ref);
      },
      onFailure: function onFailure() {
        return console.log("brightcove failed to load");
      }
    });
  };

  var getPoster = /*#__PURE__*/function () {
    var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
      var _yield$yield$fetch$js, videos;

      return _regeneratorRuntime.wrap(function _callee$(_context) {
        while (1) {
          switch (_context.prev = _context.next) {
            case 0:
              if (posterImage) {
                _context.next = 8;
                break;
              }

              _context.next = 3;
              return fetch("https://edge.api.brightcove.com/playback/v1/accounts/".concat(accountId, "/videos?q=id:").concat(videoId), {
                headers: {
                  "BCOV-Policy": policyKey
                }
              });

            case 3:
              _context.next = 5;
              return _context.sent.json();

            case 5:
              _yield$yield$fetch$js = _context.sent;
              videos = _yield$yield$fetch$js.videos;
              setPosterImage(videos[0].poster || "");

            case 8:
            case "end":
              return _context.stop();
          }
        }
      }, _callee);
    }));

    return function getPoster() {
      return _ref.apply(this, arguments);
    };
  }();

  if (loadIframeOnClick) {
    var _window3;

    if (!showVideo) {
      getPoster();
    }

    return /*#__PURE__*/React.createElement("div", {
      className: "brightcove-wrapper",
      ref: videoRef
    }, !showVideo && /*#__PURE__*/React.createElement("div", {
      onClick: /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2() {
        return _regeneratorRuntime.wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                _context2.next = 2;
                return loadLibrary();

              case 2:
                setThumbnailClicked(true);

              case 3:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }))
    }, /*#__PURE__*/React.createElement("button", {
      className: "brightcove-playBtn",
      "aria-label": "Play Video"
    }), posterImage ? /*#__PURE__*/React.createElement("img", {
      className: "brightcove-poster",
      src: posterImage,
      alt: "video"
    }) : /*#__PURE__*/React.createElement("div", {
      className: "brightcove-poster-fallback"
    })), showVideo && ((_window3 = window) === null || _window3 === void 0 ? void 0 : _window3.BrightcovePlayerLoader) && brightcoveIframe(true));
  } else if (!loadIframeOnClick && (_window4 = window) !== null && _window4 !== void 0 && _window4.BrightcovePlayerLoader) {
    return /*#__PURE__*/React.createElement("div", {
      className: "brightcove-wrapper",
      ref: videoRef
    }, brightcoveIframe());
  } else {
    return null;
  }
};

var StoryElementBrightcove = function StoryElementBrightcove(props) {
  return /*#__PURE__*/React.createElement(WithLazy, {
    margin: "0px"
  }, function () {
    return /*#__PURE__*/React.createElement(BrightcoveElement, props);
  });
};

export default StoryElementBrightcove;